import { service } from '@ember/service';
import Model, { attr, belongsTo, hasMany, type HasMany } from '@ember-data/model';
import type Store from '@ember-data/store';
import type { Type } from '@warp-drive/core-types/symbols';
import type Flow from 'invite-sign/models/flow';
import type GroupInviteLink from 'invite-sign/models/group-invite-link';
import type Location from 'invite-sign/models/location';
import type SignInField from 'invite-sign/models/sign-in-field';

// For creating new invites through a Group Invite Link.
// It borrows some logic and attributes from Invite and Entry.
export default class GroupInviteRegistration extends Model {
  declare [Type]: 'group-invite-registration';

  @service declare store: Store;

  @attr('string') declare fullName: string;
  @attr('string') declare email: string;

  @attr({ defaultValue: () => ({ 'place-id': '' }) }) declare locality: { 'place-id': string };
  @attr('boolean', { defaultValue: false }) declare additionalGuestsEnabled: boolean;
  @attr('number', { defaultValue: 0 }) declare additionalGuests: number;
  @attr('date') declare expectedArrivalTime: Date;
  @attr('string') declare purposeOfVisitName: string;
  @attr('string') declare editToken: string;

  @belongsTo('location', { async: false, inverse: null }) declare location: Location;
  @belongsTo('flow', { async: false, inverse: null }) declare flow: Flow;
  @belongsTo('group-invite-link', { async: false, inverse: null }) declare groupInviteLink: GroupInviteLink;
  @hasMany('sign-in-field', { async: false, inverse: null }) declare signInFields: HasMany<SignInField>;

  get hasValidSignInFields(): boolean {
    if (!this.fullName || !this.email) return false;

    // We don't need to validate name, host, email, purpose of visit,
    // or hidden conditional fields
    return this.signInFields
      .map(
        (field) =>
          field.isName ||
          field.isHost ||
          field.isEmail ||
          field.isPurposeOfVisit ||
          !field.isVisible ||
          field.validate(),
      )
      .every((value) => value);
  }
}
